import { CONVERSATIONS_API_URL, getConversationsAuthToken } from '@/config/env'
import type { TemplateComponent } from '@/services/whatsappService'

export interface MeetingVariableMappingEntry {
  position: number
  source_type: 'meeting'
  source_key: 'user_name' | 'property_label' | 'agent_name' | 'date' | 'time'
}

export interface MeetingAutomationSlotConfig {
  label: string
  enabled: boolean
  offset_minutes: number
  template_name: string
  template_language: string
  body_variable_count: number
  variable_mapping?: MeetingVariableMappingEntry[]
  template_status?: string
  template_body_text?: string
  template_components?: TemplateComponent[]
}

export interface MeetingAutomationSettings {
  pre_visit: MeetingAutomationSlotConfig
  post_visit: MeetingAutomationSlotConfig
  rescheduled_meetings?: number
}

class MeetingAutomationServiceClient {
  private baseUrl: string

  constructor() {
    this.baseUrl = CONVERSATIONS_API_URL || ''
  }

  private getAuthToken(): string {
    return getConversationsAuthToken()
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    const config: RequestInit = {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    }

    const token = this.getAuthToken()
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Token ${token}`,
      }
    }

    const response = await fetch(url, config)
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`)
    }

    return response.json() as Promise<T>
  }

  async getSettings(): Promise<MeetingAutomationSettings> {
    return this.request<MeetingAutomationSettings>('/api/v1/meetings/automation-settings/')
  }

  async updateSettings(payload: MeetingAutomationSettings): Promise<MeetingAutomationSettings> {
    return this.request<MeetingAutomationSettings>('/api/v1/meetings/automation-settings/', {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
  }
}

export const meetingAutomationService = new MeetingAutomationServiceClient()
